/**
 * Early Access Landing Page - Motion-Led, Logo-Centered Design
 * Reads content, layout, and motion settings from admin-configurable API
 * Trap Noir aesthetic with staggered reveals and ambient motion
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, Eye, Palette, BookOpen, Pen, Building2,
  Shield, Zap, Users, ChevronDown, ArrowRight, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { setExploreFlag } from '../components/EarlyAccessGate';

const API = process.env.REACT_APP_BACKEND_URL;

// Easing CSS values
const EASING_VALUES = {
  smooth: 'cubic-bezier(0.16, 1, 0.3, 1)',
  calm: 'cubic-bezier(0.4, 0, 0.2, 1)',
  snappy: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

// Spacing values for sections
const SPACING_VALUES = {
  tight: { py: 'py-16 md:py-20', gap: 'gap-4' },
  normal: { py: 'py-24 md:py-32', gap: 'gap-6' },
  roomy: { py: 'py-32 md:py-40', gap: 'gap-8' },
};

// Default settings (fallback)
const DEFAULT_SETTINGS = {
  content: {
    early_access_label: "Early Access",
    tagline: "Trap Comix costs you nothing to test, but not being discoverable costs you everything.",
    primary_cta_label: "Start Creating",
    primary_cta_destination: "/auth?mode=signup",
    secondary_cta_label: "Explore Platform",
    secondary_cta_destination: "/"
  },
  layout: {
    sections: [
      { id: "hero", label: "Hero Section", enabled: true, order: 1 },
      { id: "content_types", label: "Content Types", enabled: true, order: 2 },
      { id: "benefits", label: "Why Join", enabled: true, order: 3 },
      { id: "build_phase", label: "Build Phase", enabled: true, order: 4 },
      { id: "creators", label: "Join Movement", enabled: true, order: 5 },
      { id: "transparency", label: "Transparency", enabled: true, order: 6 }
    ],
    spacing_preset: "normal"
  },
  motion: {
    hero_duration: 700,
    stagger_delay: 150,
    easing_preset: "smooth",
    respect_reduced_motion: true
  }
};

// Hook for scroll-triggered animations
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [options]);

  return [ref, isInView];
};

// Staggered animation wrapper
const AnimateOnScroll = ({ children, delay = 0, className = '', duration = 700 }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`,
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const EarlyAccessPage = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch settings from API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API}/api/landing-page/settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({
            content: { ...prev.content, ...data.content },
            layout: { ...prev.layout, ...data.layout },
            motion: { ...prev.motion, ...data.motion }
          }));
        }
      } catch (error) {
        console.warn('Failed to fetch landing page settings, using defaults:', error);
      } finally {
        setSettingsLoaded(true);
      }
    };
    
    fetchSettings();
  }, []);

  useEffect(() => {
    document.title = 'Early Access - Creator Build Phase | Trap Comix';
    setMounted(true);
    
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Derive animation values from settings
  const motionConfig = useMemo(() => {
    const { hero_duration, stagger_delay, easing_preset, respect_reduced_motion } = settings.motion;
    const disableMotion = respect_reduced_motion && prefersReducedMotion;
    
    return {
      duration: disableMotion ? 0 : hero_duration,
      stagger: disableMotion ? 0 : stagger_delay,
      easing: EASING_VALUES[easing_preset] || EASING_VALUES.smooth,
      disabled: disableMotion,
    };
  }, [settings.motion, prefersReducedMotion]);

  // Derive spacing from settings
  const spacing = useMemo(() => {
    return SPACING_VALUES[settings.layout.spacing_preset] || SPACING_VALUES.normal;
  }, [settings.layout.spacing_preset]);

  // Check if section is enabled
  const isSectionEnabled = (sectionId) => {
    const section = settings.layout.sections.find(s => s.id === sectionId);
    return section?.enabled !== false;
  };

  // Get sorted enabled sections
  const enabledSections = useMemo(() => {
    return settings.layout.sections
      .filter(s => s.enabled)
      .sort((a, b) => a.order - b.order);
  }, [settings.layout.sections]);

  const handleEnterEarlyAccess = () => {
    setExploreFlag();
    navigate(settings.content.secondary_cta_destination || '/');
  };

  const handleJoinAsCreator = () => {
    setExploreFlag();
    navigate(settings.content.primary_cta_destination || '/auth?mode=signup');
  };

  const scrollToContent = () => {
    document.getElementById('content-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // CSS Variables for animations (injected dynamically)
  const cssVars = {
    '--trap-bg': '#050505',
    '--trap-bg-subtle': '#0A0A0A',
    '--trap-card': '#111111',
    '--trap-orange': '#FF5500',
    '--trap-orange-hover': '#FF7700',
    '--trap-lime': '#CCFF00',
    '--trap-border': '#27272A',
    '--trap-glow': 'rgba(255, 85, 0, 0.5)',
    '--logo-size-mobile': '280px',
    '--logo-size-tablet': '380px',
    '--logo-size-desktop': '480px',
    // Animation tokens from settings
    '--hero-duration': `${motionConfig.duration}ms`,
    '--hero-stagger': `${motionConfig.stagger}ms`,
    '--hero-easing': motionConfig.easing,
  };

  // Content Types for Bento Grid
  const contentTypes = [
    {
      title: 'Comics & Manga',
      description: 'Sequential art with vertical scroll reader',
      icon: Palette,
      span: 'md:col-span-2 md:row-span-2',
      image: 'https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=800&auto=format&fit=crop&q=60',
      featured: true,
    },
    {
      title: 'Novels',
      description: 'Long-form stories with rich text',
      icon: BookOpen,
      span: 'md:col-span-1 md:row-span-1',
      image: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&auto=format&fit=crop&q=60',
    },
    {
      title: 'Artwork',
      description: 'Single illustrations and art pieces',
      icon: Pen,
      span: 'md:col-span-1 md:row-span-1',
      image: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&auto=format&fit=crop&q=60',
    },
  ];

  // Benefits
  const benefits = [
    {
      icon: Shield,
      title: 'Own Your IP',
      description: 'Your stories, your characters, your rights. Always.',
    },
    {
      icon: Zap,
      title: 'No Algorithm Lottery',
      description: 'Discovery is intentional, not random chance.',
    },
    {
      icon: Users,
      title: 'Build Early',
      description: 'Shape the platform. Define the culture.',
    },
  ];

  // Build Phase Steps
  const buildSteps = [
    { number: '01', title: 'Create Account', description: 'Set up your creator profile' },
    { number: '02', title: 'Upload Work', description: 'Comics, novels, or artwork' },
    { number: '03', title: 'Get Discovered', description: 'Reach your audience' },
  ];

  // Show loading state until settings are loaded
  if (!settingsLoaded) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // Hero animation style helper
  const getHeroAnimationStyle = (index) => {
    if (motionConfig.disabled) {
      return { opacity: mounted ? 1 : 0 };
    }
    
    const delay = index * motionConfig.stagger;
    return {
      opacity: mounted ? 1 : 0,
      transform: mounted ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.97)',
      transition: `opacity ${motionConfig.duration}ms ${motionConfig.easing}, transform ${motionConfig.duration}ms ${motionConfig.easing}`,
      transitionDelay: `${delay}ms`,
    };
  };

  return (
    <div 
      className="min-h-screen bg-[#050505] text-white font-outfit overflow-x-hidden"
      style={cssVars}
    >
      {/* ===== HERO SECTION ===== */}
      {isSectionEnabled('hero') && (
        <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden">
          {/* Ambient Background Glow */}
          <div 
            className={`absolute inset-0 ${motionConfig.disabled ? '' : 'animate-glow-pulse'}`}
            style={{
              background: 'radial-gradient(circle at 50% 50%, rgba(255, 85, 0, 0.12), transparent 60%)',
            }}
          />
          
          {/* Subtle Grid Overlay */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />

          {/* Early Access Label - Animates first (index 0) */}
          <p 
            className="relative z-10 mb-6 text-xs md:text-sm text-zinc-500 tracking-[0.25em] uppercase font-light"
            style={getHeroAnimationStyle(0)}
          >
            {settings.content.early_access_label}
          </p>

          {/* Logo Image - Animates second (index 1) */}
          <div 
            className="relative z-10 flex items-center justify-center"
            style={getHeroAnimationStyle(1)}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_trapcomix-admin-1/artifacts/bzq9nf1k_Trap%20Comix%20LOGO%20Web.png"
              alt="Trap Comix"
              className="w-[var(--logo-size-mobile)] sm:w-[var(--logo-size-tablet)] lg:w-[var(--logo-size-desktop)] h-auto select-none"
              style={{
                filter: 'drop-shadow(0 0 60px rgba(255, 85, 0, 0.4))',
                maxWidth: '90vw',
              }}
              draggable="false"
            />
          </div>

          {/* Tagline - Animates third (index 2) */}
          <p 
            className="relative z-10 mt-8 md:mt-10 text-zinc-400 font-light text-center max-w-[92vw] sm:max-w-xl md:max-w-2xl lg:max-w-3xl px-4"
            style={{
              ...getHeroAnimationStyle(2),
              fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
              lineHeight: '1.6',
              letterSpacing: '0.02em',
            }}
          >
            {settings.content.tagline}
          </p>

          {/* CTA Buttons - Animates fourth (index 3) */}
          <div 
            className="relative z-10 mt-10 md:mt-12 flex flex-col sm:flex-row gap-4 sm:gap-6 px-6"
            style={getHeroAnimationStyle(3)}
          >
            <Button
              onClick={handleJoinAsCreator}
              className="bg-[#FF5500] hover:bg-[#FF7700] text-white font-bold uppercase tracking-wider px-8 py-6 text-base rounded-none transition-all hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(255,85,0,0.4)] hover:shadow-[0_0_40px_rgba(255,85,0,0.6)]"
              data-testid="join-as-creator-btn"
            >
              <Upload className="w-5 h-5 mr-3" />
              {settings.content.primary_cta_label}
            </Button>
            
            <Button
              onClick={handleEnterEarlyAccess}
              variant="outline"
              className="bg-transparent border border-zinc-700 hover:border-white hover:bg-white/5 text-white font-bold uppercase tracking-wider px-8 py-6 text-base rounded-none transition-all"
              data-testid="enter-early-access-btn"
            >
              <Eye className="w-5 h-5 mr-3" />
              {settings.content.secondary_cta_label}
            </Button>
          </div>

          {/* Scroll Indicator */}
          <button
            onClick={scrollToContent}
            className={`absolute bottom-8 z-10 flex flex-col items-center text-zinc-500 hover:text-white transition-colors ${motionConfig.disabled ? '' : 'animate-float'}`}
            aria-label="Scroll to content"
          >
            <span className="text-xs uppercase tracking-widest mb-2">Scroll</span>
            <ChevronDown className="w-5 h-5" />
          </button>
        </section>
      )}

      {/* ===== CONTENT TYPES SECTION ===== */}
      {isSectionEnabled('content_types') && (
        <section id="content-section" className={`${spacing.py} px-6 md:px-12 max-w-7xl mx-auto`}>
          <AnimateOnScroll duration={motionConfig.duration}>
            <h2 className="font-anton text-4xl md:text-6xl tracking-tight uppercase text-center mb-4">
              Create. Publish. <span className="text-[#FF5500]">Earn.</span>
            </h2>
            <p className="text-zinc-400 text-center text-lg mb-16 max-w-2xl mx-auto">
              Everything you need to share your stories with the world.
            </p>
          </AnimateOnScroll>

          {/* Bento Grid */}
          <div className={`grid grid-cols-1 md:grid-cols-3 ${spacing.gap} auto-rows-[200px] md:auto-rows-[250px]`}>
            {contentTypes.map((type, index) => (
              <AnimateOnScroll 
                key={type.title} 
                delay={index * 100}
                duration={motionConfig.duration}
                className={type.span}
              >
                <div 
                  className={`relative h-full bg-[#111] border border-zinc-800 overflow-hidden group cursor-pointer
                    hover:border-[#FF5500] transition-all duration-300 ${type.featured ? 'md:row-span-2' : ''}`}
                >
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                    style={{ backgroundImage: `url(${type.image})` }}
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
                  
                  {/* Content */}
                  <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
                    <type.icon className="w-8 h-8 text-[#FF5500] mb-3" />
                    <h3 className="font-anton text-2xl md:text-3xl uppercase tracking-tight">
                      {type.title}
                    </h3>
                    <p className="text-zinc-400 mt-2 text-sm md:text-base">
                      {type.description}
                    </p>
                  </div>
                  
                  {/* Hover Glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{ background: 'linear-gradient(180deg, rgba(255,85,0,0.05) 0%, rgba(255,85,0,0) 50%)' }}
                  />
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </section>
      )}

      {/* ===== WHY JOIN SECTION ===== */}
      {isSectionEnabled('benefits') && (
        <section className={`${spacing.py} px-6 md:px-12 bg-[#0A0A0A]`}>
          <div className="max-w-7xl mx-auto">
            <AnimateOnScroll duration={motionConfig.duration}>
              <h2 className="font-anton text-4xl md:text-6xl tracking-tight uppercase text-center mb-16">
                Why <span className="text-[#CCFF00]">Join?</span>
              </h2>
            </AnimateOnScroll>

            <div className={`grid grid-cols-1 md:grid-cols-3 ${spacing.gap} md:gap-8`}>
              {benefits.map((benefit, index) => (
                <AnimateOnScroll key={benefit.title} delay={index * 150} duration={motionConfig.duration}>
                  <div className="bg-[#111] border border-zinc-800 p-8 md:p-10 hover:border-[#CCFF00]/50 transition-all duration-300 group h-full">
                    <div className="w-14 h-14 bg-[#CCFF00]/10 flex items-center justify-center mb-6 group-hover:bg-[#CCFF00]/20 transition-colors">
                      <benefit.icon className="w-7 h-7 text-[#CCFF00]" />
                    </div>
                    <h3 className="font-anton text-2xl uppercase tracking-tight mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-zinc-400 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </AnimateOnScroll>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== BUILD PHASE SECTION ===== */}
      {isSectionEnabled('build_phase') && (
        <section className={`${spacing.py} px-6 md:px-12`}>
          <div className="max-w-7xl mx-auto">
            <AnimateOnScroll duration={motionConfig.duration}>
              <div className="text-center mb-16">
                <span className="inline-block px-4 py-2 bg-[#FF5500]/10 border border-[#FF5500]/30 text-[#FF5500] text-sm uppercase tracking-widest mb-6">
                  Early Access
                </span>
                <h2 className="font-anton text-4xl md:text-6xl tracking-tight uppercase">
                  Build Phase
                </h2>
                <p className="text-zinc-400 text-lg mt-4 max-w-xl mx-auto">
                  Three steps to launch your creative journey
                </p>
              </div>
            </AnimateOnScroll>

            {/* Steps */}
            <div className="relative">
              {/* Connector Line (desktop only) */}
              <div className="hidden md:block absolute top-1/2 left-0 w-full h-[1px] bg-zinc-800 -translate-y-1/2 -z-10" />
              
              <div className="flex flex-col md:flex-row justify-between items-center gap-12 md:gap-8">
                {buildSteps.map((step, index) => (
                  <AnimateOnScroll key={step.number} delay={index * 200} duration={motionConfig.duration} className="flex-1 w-full md:w-auto">
                    <div className="text-center group">
                      {/* Number Circle */}
                      <div className="relative inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 bg-[#050505] border-2 border-zinc-700 group-hover:border-[#FF5500] transition-colors duration-300 mb-6">
                        <span className="font-mono text-2xl md:text-3xl text-zinc-500 group-hover:text-[#FF5500] transition-colors">
                          {step.number}
                        </span>
                      </div>
                      
                      <h3 className="font-anton text-xl md:text-2xl uppercase tracking-tight mb-2">
                        {step.title}
                      </h3>
                      <p className="text-zinc-500 text-sm md:text-base">
                        {step.description}
                      </p>
                    </div>
                  </AnimateOnScroll>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== FEATURED CREATORS SECTION ===== */}
      {isSectionEnabled('creators') && (
        <section className={`${spacing.py} px-6 md:px-12 bg-[#0A0A0A]`}>
          <div className="max-w-7xl mx-auto">
            <AnimateOnScroll duration={motionConfig.duration}>
              <h2 className="font-anton text-4xl md:text-6xl tracking-tight uppercase text-center mb-6">
                Join The <span className="text-[#FF5500]">Movement</span>
              </h2>
              <p className="text-zinc-400 text-center text-lg mb-16 max-w-2xl mx-auto">
                Black & Brown creators are building something new. Be part of it from day one.
              </p>
            </AnimateOnScroll>

            {/* Creator Types Grid */}
            <div className={`grid grid-cols-2 md:grid-cols-4 ${spacing.gap} mb-16`}>
              {[
                { icon: Palette, label: 'Comic Artists' },
                { icon: Pen, label: 'Writers' },
                { icon: BookOpen, label: 'Novelists' },
                { icon: Building2, label: 'Indie Publishers' },
              ].map((item, index) => (
                <AnimateOnScroll key={item.label} delay={index * 100} duration={motionConfig.duration}>
                  <div className="bg-[#111] border border-zinc-800 p-6 text-center hover:border-zinc-600 transition-colors group">
                    <item.icon className="w-8 h-8 text-zinc-500 group-hover:text-white mx-auto mb-3 transition-colors" />
                    <span className="text-sm uppercase tracking-wider text-zinc-400 group-hover:text-white transition-colors">
                      {item.label}
                    </span>
                  </div>
                </AnimateOnScroll>
              ))}
            </div>

            {/* Final CTA */}
            <AnimateOnScroll delay={300} duration={motionConfig.duration}>
              <div className="text-center">
                <Button
                  onClick={handleJoinAsCreator}
                  className="bg-[#FF5500] hover:bg-[#FF7700] text-white font-bold uppercase tracking-wider px-12 py-6 text-lg rounded-none transition-all hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(255,85,0,0.4)]"
                >
                  {settings.content.primary_cta_label}
                  <ArrowRight className="w-5 h-5 ml-3" />
                </Button>
              </div>
            </AnimateOnScroll>
          </div>
        </section>
      )}

      {/* ===== TRANSPARENCY FOOTER ===== */}
      {isSectionEnabled('transparency') && (
        <section className="py-16 px-6 md:px-12 border-t border-zinc-900">
          <div className="max-w-4xl mx-auto">
            <AnimateOnScroll duration={motionConfig.duration}>
              <div className="bg-[#111] border border-zinc-800 p-8 md:p-10">
                <h3 className="font-anton text-xl md:text-2xl uppercase tracking-tight text-[#FF5500] mb-4">
                  What &ldquo;Early Access&rdquo; Means
                </h3>
                <ul className="space-y-3 text-zinc-400">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full mt-2 flex-shrink-0" />
                    <span>Some features are still being refined</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full mt-2 flex-shrink-0" />
                    <span>You may see placeholder or test accounts</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full mt-2 flex-shrink-0" />
                    <span>The platform will visibly evolve</span>
                  </li>
                </ul>
                <p className="text-white mt-6 font-medium">
                  You&apos;re helping us build this the right way.
                </p>
              </div>
            </AnimateOnScroll>
          </div>
        </section>
      )}

      {/* ===== FOOTER ===== */}
      <footer className="py-8 text-center border-t border-zinc-900">
        <p className="text-sm text-zinc-600">
          © Trap Comix — Creator Build Phase
        </p>
        <p className="text-xs text-[#FF5500]/60 mt-2">
          Built with the community, not above it.
        </p>
      </footer>
    </div>
  );
};

export default EarlyAccessPage;
